import React, { useEffect, useState } from "react";
import { Trash2, ChevronDown, ChevronRight, FileText, Mail, Phone, Video, Check, RotateCcw } from "lucide-react";
import { adminApi } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const contactIcon = (c) => (c === "Phone" ? Phone : c === "Video call" ? Video : Mail);
const notifyInbox = () => window.dispatchEvent(new Event("inbox-updated"));

export default function QuotesManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [deleteId, setDeleteId] = useState(null);

  const load = () => { setLoading(true); adminApi.quotes.list().then(setItems).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const handleDelete = async () => {
    try { await adminApi.quotes.remove(deleteId); toast({ title: "Quote request removed" }); load(); notifyInbox(); }
    catch { toast({ title: "Error removing", variant: "destructive" }); }
    finally { setDeleteId(null); }
  };

  const toggleDealt = async (q) => {
    const next = !q.dealt_with;
    setItems((list) => list.map((it) => (it.id === q.id ? { ...it, dealt_with: next } : it)));
    try { await adminApi.quotes.setDealt(q.id, next); notifyInbox(); toast({ title: next ? "Marked as dealt with" : "Reopened" }); }
    catch { toast({ title: "Couldn't update", variant: "destructive" }); load(); }
  };

  const outstanding = items.filter((q) => !q.dealt_with).length;

  return (
    <div>
      <h1 className="font-display font-extrabold text-2xl text-navy mb-1">Quote Requests</h1>
      <p className="text-sm text-slate-500 mb-6">{items.length} request(s) • <span className="font-semibold text-orange">{outstanding} to deal with</span>. Click a card to see the full answers.</p>

      {loading ? <p className="text-slate-400">Loading...</p> : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center text-slate-400"><FileText size={36} className="mx-auto mb-3 text-slate-300" />No quote requests yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((q) => {
            const isOpen = open[q.id];
            const CIcon = contactIcon(q.contact_preference);
            return (
              <div key={q.id} data-testid={`quote-${q.id}`} className={`rounded-xl border overflow-hidden ${q.dealt_with ? "bg-slate-50/70 border-slate-100" : "bg-white border-slate-100"}`}>
                <div className="p-4 flex items-center gap-4">
                  <button onClick={() => setOpen((o) => ({ ...o, [q.id]: !o[q.id] }))} className="text-slate-400 hover:text-navy shrink-0">{isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</button>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-navy truncate flex items-center gap-2">
                      {q.name}{q.business_name ? ` · ${q.business_name}` : ""}
                      {q.dealt_with && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1 shrink-0"><Check size={11} /> Dealt with</span>}
                    </p>
                    <p className="text-sm text-slate-400 truncate">{q.reason || "—"}</p>
                  </div>
                  <span className="text-xs text-slate-400 shrink-0 hidden md:block">{fmt(q.created_at)}</span>
                  <button
                    data-testid={`quote-dealt-${q.id}`}
                    onClick={() => toggleDealt(q)}
                    title={q.dealt_with ? "Reopen" : "Mark as dealt with"}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${q.dealt_with ? "bg-slate-100 hover:bg-slate-200 text-slate-500" : "bg-green-50 hover:bg-green-100 text-green-600"}`}
                  >
                    {q.dealt_with ? <RotateCcw size={15} /> : <Check size={16} />}
                  </button>
                  <button onClick={() => setDeleteId(q.id)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 shrink-0"><Trash2 size={15} /></button>
                </div>
                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/60 p-5 space-y-4 text-sm">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Info label="Email"><a href={`mailto:${q.email}`} className="text-orange font-semibold">{q.email}</a></Info>
                      <Info label="Phone">{q.phone || "—"}</Info>
                      <Info label="Preferred contact"><span className="inline-flex items-center gap-1.5"><CIcon size={13} /> {q.contact_preference || "—"}</span></Info>
                      <Info label="Business stage">{q.business_stage || "—"}</Info>
                      <Info label="Industry">{q.industry || "—"}</Info>
                    </div>
                    <Info label="Goals">{q.goals?.length ? (
                      <div className="flex flex-wrap gap-1.5 mt-1">{q.goals.map((g) => <span key={g} className="bg-orange/10 text-orange text-xs font-semibold px-2.5 py-1 rounded-full">{g}</span>)}</div>
                    ) : "—"}</Info>
                    <Info label="What's frustrating them">{q.frustration || "—"}</Info>
                    <Info label="Their ideal accountant">{q.ideal_accountant || "—"}</Info>
                    <Info label="Anything else">{q.anything_else || "—"}</Info>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this quote request?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-full">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Info = ({ label, children }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-0.5">{label}</p>
    <div className="text-navy leading-relaxed whitespace-pre-wrap">{children}</div>
  </div>
);
